import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { useEffect, useState } from "react";
import { Button, Card, Table } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import api from "../../config/api";
import { getDate, LocalFormatDate, parseAmount, rupeeFormat } from "../../config/functions";
import {
  ClientRegistrationType,
  fetchClientRegistrationAction,
} from "../../redux/actions/clientRegistration";
import { AppDispatch, StoreState } from "../../redux/store";
import GstSalesReportFilter from "./GstSalesReportFilter";

interface InvoiceData {
  financialYear: string;
  hsnCode: string;
  productName: string;
  uoq: string;
  qty: number;
  invValue: number;
  rate: number;
  taxableValue: number;
  igst: number;
  cgst: number;
  sgst: number;
  cessAmount: number;
}

const GstSalesHsnReport = () => {
  const dispatch = useDispatch<AppDispatch>();

  const [fromDate, setFromDate] = useState<string>("");
  const [toDate, setToDate] = useState<string>("");
  const [branchId, setBranchId] = useState<string>("");
  const [customerType, setCustomerType] = useState<string>("");
  const [tableData, setTableData] = useState<InvoiceData[]>([]);
  const [triggerSearch, setTriggerSearch] = useState<boolean>(false);

  const clients = useSelector<StoreState, ClientRegistrationType[]>(
    (state) => state.clientRegistration,
  );

  useEffect(() => {
    dispatch(fetchClientRegistrationAction());
  }, []);

  useEffect(() => {
    if (triggerSearch) {
      api()
        .get(
          `reports/gstHsnSalesWise/?fromDate=${getDate(fromDate)}&toDate=${getDate(
            toDate,
          )}&branchId=${branchId}&customerType=${customerType}`,
        )
        .then((res) => {
          if (res.status === 200 && res.data.data) {
            setTableData(res.data.data);
          }
        })
        .catch((error) => {
          console.error("Error fetching sales data:", error);
        });

      setTriggerSearch(false);
    }
  }, [triggerSearch, fromDate, toDate, branchId, customerType]);

  const [filters, setFilters] = useState<any>({});

  const handleSearch = (newFilters: any) => {
    setFilters(newFilters);
    setFromDate(newFilters.fromDate);
    setToDate(newFilters.toDate);
    setBranchId(newFilters.branchId);
    setCustomerType(newFilters.customerType);
    setTriggerSearch(true);
  };

  const uniqueHsnCount = new Set(
    (tableData || [])
      .map((r) => (r.hsnCode || "").trim())
      .filter((hsn) => hsn.length > 0)
  ).size;

  const invValue = (tableData || []).reduce(
    (sum, r) => sum + parseAmount(r.invValue),
    0,
  );
  const totalTaxableValue = (tableData || []).reduce(
    (sum, r) => sum + parseAmount(r.taxableValue),
    0,
  );
  const totalCgst = (tableData || []).reduce(
    (sum, r) => sum + parseAmount(r.cgst),
    0,
  );
  const totalSgst = (tableData || []).reduce(
    (sum, r) => sum + parseAmount(r.sgst),
    0,
  );
  const qty = (tableData || []).reduce(
    (sum, r) => sum + parseAmount(r.qty),
    0,
  );
  const totalCessAmount = (tableData || []).reduce(
    (sum, r) => sum + parseAmount(r.cessAmount),
    0,
  );

  const toRow = (item: InvoiceData, index: number) => [
    String(index + 1),
    item.hsnCode ?? "",
    item.productName ?? "",
    item.uoq ?? "",
    String(item.qty ?? ""),
    rupeeFormat(item.invValue) ?? "0",
    item.rate ?? "",
    rupeeFormat(item.taxableValue) ?? "0",
    item.igst ?? "",
    rupeeFormat(item.cgst) ?? "0",
    rupeeFormat(item.sgst) ?? "0",
    // rupeeFormat(item.cessAmount) ?? "0",
  ];

  const downloadCSV = () => {
    const headerLabels = [
      "SI.No",
      "HSN Code",
      "Product Name",
      "UOQ",
      "Total QTY",
      "Total Value",
      "Rate",
      "Taxable Value",
      "IGST",
      "CGST",
      "SGST",
      // "Cess Amount",
    ];

    const rows = tableData?.map((item, idx) => toRow(item, idx)) || [];

    rows.push([
      "", // SI.No
      `Unique HSN: ${uniqueHsnCount}`, // HSN Code
      "", // Product Name
      "", // UOQ
      String(qty), // Total QTY
      rupeeFormat(invValue) ?? "0", // Total Value
      "", // Rate
      rupeeFormat(totalTaxableValue) ?? "0", // Taxable Value
      "", // IGST
      rupeeFormat(totalCgst) ?? "0", // CGST
      rupeeFormat(totalSgst) ?? "0", // SGST
      // rupeeFormat(totalCessAmount) ?? "0", // Cess Amount
    ]);

    const csvCell = (value: unknown) => {
      const s = value === null || value === undefined ? "" : String(value);
      return `"${s.replace(/"/g, '""')}"`;
    };

    const csvContent = [
      headerLabels.map(csvCell).join(","),
      ...rows.map((row) => row.map(csvCell).join(",")),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `TAP_sales_${customerType} _${LocalFormatDate(getDate(filters?.fromDate)) || ""}_${LocalFormatDate(getDate(filters?.toDate)) || ""}`;
    link.click();
  };

  const downloadPDF = () => {
    const logoUrl = clients[0]?.shopPhoto;

    const doc = new jsPDF("landscape");
    const marginX = 10;
    const boxWidth = doc.internal.pageSize.width - 2 * marginX;
    const boxHeight = 25;
    let startY = 15;

    doc.setFontSize(10);
    doc.setFont("helvetica", "bold");

    doc.rect(marginX, startY - 5, boxWidth, boxHeight);

    if (logoUrl) {
      doc.addImage(logoUrl, "JPEG", marginX + 5, startY - 2, 25, 20);
    }

    const paddingBetweenHeaderAndTable = 3;
    const textX = marginX + 35;

    doc.text(clients[0]?.shopFullName || "", textX, startY);
    doc.setFont("helvetica", "normal");
    doc.text(
      `${clients[0]?.address1 || ""}, ${clients[0]?.address2 || ""}, ${
        clients[0]?.district || ""
      }`,
      textX,
      startY + 5,
    );
    doc.text(
      `${clients[0]?.state || ""} - ${clients[0]?.pincode || ""}, ${
        clients[0]?.contactNo1 || ""
      }`,
      textX,
      startY + 10,
    );
    doc.text(`GSTIN: ${clients[0]?.gstNo || ""}`, textX, startY + 15);

    doc.setFont("helvetica", "bold");
    doc.setFontSize(14);
    const heading = `GST ${customerType} Sales Hsn report`;
    const headingWidth = doc.getTextWidth(heading);
    const pageWidth = doc.internal.pageSize.width;
    doc.text(heading, marginX + boxWidth - headingWidth - 10, startY + 8);

    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    const financialYearText = `Financial Year: ${
      tableData[0]?.financialYear || ""
    }`;
    const headingStartX = marginX + boxWidth - headingWidth - 10;
    doc.text(financialYearText, headingStartX, startY + 13);

    doc.setFontSize(10);
    doc.setFont("helvetica", "bold");
    const fromDate =
      "From : " + (LocalFormatDate(getDate(filters?.fromDate)) || "");
    const toDate = "To : " + (LocalFormatDate(getDate(filters?.toDate)) || "");

    const tableStartY = startY + boxHeight + paddingBetweenHeaderAndTable;
    doc.text(fromDate, marginX, tableStartY);
    doc.text(toDate, marginX + 50, tableStartY);

    autoTable(doc, {
      startY: 47,
      showFoot: "lastPage",
      head: [
        [
        "SI.No",
      "HSN Code",
      "Product Name",
      "UOQ",
      "Total QTY",
      "Total Value",
      "Rate",
      "Taxable Value",
      "IGST",
      "CGST",
      "SGST",
      // "Cess Amount",
        ],
      ],
      body: (tableData || []).map((item, idx) => toRow(item, idx)),
      foot: [
        [
          "",
          ` ${uniqueHsnCount}`,
          "",
          "",
          qty ?? "0",
          rupeeFormat(invValue) ?? "0",
          "",
          rupeeFormat(totalTaxableValue) ?? "0",
          "",
          rupeeFormat(totalCgst) ?? "0",
          rupeeFormat(totalSgst) ?? "0",
          // "",
        ],
      ],
      theme: "grid",
      styles: { fontSize: 8, textColor: 0, lineColor: 0, lineWidth: 0.1 },
      headStyles: {
        fillColor: [255, 255, 255],
        textColor: 0,
        fontStyle: "bold",
        lineColor: 0,
        lineWidth: 0.1,
      },
      footStyles: {
        fillColor: [255, 255, 255],
        halign: "right",
      },
      margin: { top: 25, left: 10, right: 10 },
      columnStyles: {
        0: { halign: "center" }, // SI.No
        1: { halign: "left" }, // HSN Code
        2: { halign: "left" }, // Product Name
        3: { halign: "left" }, // UOQ
        4: { halign: "right" }, // Total QTY
        5: { halign: "right" }, // Total Value
        6: { halign: "right" }, // Rate
        7: { halign: "right" }, // Taxable Value
        8: { halign: "right" }, // IGST
        9: { halign: "right" }, // CGST
        10: { halign: "right" }, // SGST
        // 11: { halign: "right" }, // Cess Amount
      },
    });

    // Footer Section (Page Number)
    const pageCount = doc.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      let pageHeight = doc.internal.pageSize.height;
      let footerY = pageHeight - 11;

      doc.setFontSize(10);
      doc.text(`Page ${i} of ${pageCount}`, pageWidth - 30, footerY);
    }

    doc.save(
      `TAP_sales_${customerType} _${LocalFormatDate(getDate(filters?.fromDate)) || ""}_${LocalFormatDate(getDate(filters?.toDate)) || ""}.pdf`,
    );
  };
  // tableData.map((item, index) => {
  //     console.log(item.quantity);
  // });

  return (
    <>
      <GstSalesReportFilter onSearch={handleSearch} />
      {tableData.length > 0 ? (
        <Card className="mt-2 p-2 shadow-lg border-0 rounded-3">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <h5 className="fw-bold">Sales invoice details</h5>
            <div>
              <Button
                className="me-2 btn-sm"
                variant="success"
                onClick={downloadCSV}
              >
                Download CSV
              </Button>
              <Button
                className="me-2 btn-sm"
                variant="success"
                onClick={downloadPDF}
              >
                Download PDF
              </Button>
            </div>
          </div>
          <div>Total :{rupeeFormat(invValue)}</div>
          <Table striped bordered hover>
            <thead>
              <tr>
                <th>SI.No</th>
                <th>HSN Code</th>
                <th>Product Name</th>
                <th>UOQ</th>
                <th>QTY</th>
                <th>Invoice Value</th>
                <th>Rate</th>
                <th>Taxable Value</th>
                <th>IGST</th>
                <th>CGST</th>
                <th>SGST</th>
                {/* <th>Cess Amount </th> */}
              </tr>
            </thead>

            <tbody>
              {tableData?.map((item, index) => (
                <tr key={index}>
                  <td>{index + 1}</td>
                  <td>{item.hsnCode}</td>
                  <td>{item.productName}</td>
                  <td>{item.uoq}</td>
                  <td>{item.qty}</td>
                  <td>{rupeeFormat(item.invValue)}</td>
                  <td>{item.rate}</td>
                  <td>{rupeeFormat(item.taxableValue)}</td>
                  <td>{item.igst}</td>
                  <td>{rupeeFormat(item.cgst)}</td>
                  <td>{rupeeFormat(item.sgst)}</td>
                  {/* <td>{rupeeFormat(item.cessAmount)}</td> */}
                </tr>
              ))}
            </tbody>
          </Table>
        </Card>
      ) : (
        <h4 className="text-center fw-bold text-secondary">
          No such data found!
        </h4>
      )}
    </>
  );
};

export default GstSalesHsnReport;
